import React, {useRef, useState} from "react";
import {Box, Button, CircularProgress, FormControl, Link, TextField, Typography} from "@mui/material";
import {axios_tenant} from "../../../axios";
import * as ExcelJS from "exceljs";
import {toast} from "react-toastify";
import {useDropzone} from "react-dropzone";
import Div from "@jumbo/shared/Div/Div";
import ResultsSummaryTable from "app/components/ResultsSummaryTable";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import JSZip from "jszip";

var workbook = {};

const QC_FileUpload = () => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [textData, setTextData] = useState("");
    const [appNo, setAppNo] = useState("");

    const [results, setResults] = useState([]);
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(0); //setting null start value

    const [newWorkbook, setNewWorkbook] = useState({});

    const [open, setOpen] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [message, setMessage] = useState("");

    const handleTextChange = e => {
        setTextData(e.target.value);
    };

    const requiredDocuments = [
        { keyword: "1390", key: "1390", name: "Transmittal Letter" },
        { keyword: "ads", key: "ads", name: "Application Data Sheet" },
        { keyword: "prelim", key: "prelim", name: "Preliminary Amendment" },
        { keyword: "spec", key: "spec", name: "Specification" },
        { keyword: "pct", key: "publication", name: "PCT Publication" },
        { keyword: "declaration", key: "declaration", name: "Declarations" }
    ];

    const readFileAsync = file => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file);
        });
    };

    const handleUpload = async () => {
        const missingDocuments = [...requiredDocuments];

        if (uploadedFiles.length > 0 && textData) {
            for (let i = 0; i < uploadedFiles.length; i++) {
                const fileName = uploadedFiles[i].name;
                const docPresent = requiredDocuments.find(doc => fileName.toLowerCase().includes(doc.key));

                if (docPresent) {
                    const index = missingDocuments.findIndex(doc => doc.key === docPresent.key);
                    if (index !== -1) missingDocuments.splice(index, 1);
                }
            }

            if (missingDocuments.length > 0) {
                const missingDocumentNames = missingDocuments.map(doc => doc.name).join(", ");
                const message = missingDocumentNames;
                setMessage(message);

                setOpen(true);
            } else {
                setLoading(1);
                verifyRequest();
            }
        }
    };

    const verifyRequest = () => {
        const formData = new FormData();

        formData.append("publicationNo", textData);

        for (let i = 0; i < uploadedFiles.length; i++) {
            formData.append("files", uploadedFiles[i]);
        }

        const currentDate = new Date();

        // const time = currentDate.getTime();

        axios_tenant
        .post("/qc/verify", formData)
            .then(res => {
                setAppNo(res.data.applicationNo);

                generateExcel(res.data.results, res.data.applicationNo);
                const errorResults = generateSummary(res.data.results);

                postArchiveData(res.data.applicationNo, errorResults.length, res.data.results, currentDate.toJSON());

                toast.success("Results Summary generated successfully");
                setLoading(2); //setting loading value to false
            })
            .catch(err => {
                setLoading(3); // setting loading value to error

                // if (err?.response?.data?.message) toast.error(err.response.data.message)
                // else if (err?.message) toast.error(err.message);
                // else toast.error("Something went wrong, please check your input and try again");
            });
    };

    const postArchiveData = async (applicationNo, noOfErrors, results, runOn) => {
        const zip = new JSZip();
        const archiveFormData = new FormData();
        const packageName = uploadedFiles[0].webkitRelativePath.split("/")[0];

        archiveFormData.append("applicationNo", applicationNo);
        archiveFormData.append("packageName", packageName);
        archiveFormData.append("noOfErrors", noOfErrors);
        archiveFormData.append("results", JSON.stringify(results));
        archiveFormData.append("runOn", runOn);

        for (let i = 0; i < uploadedFiles.length; i++) {
            const file = uploadedFiles[i];
            const relativePath = file.webkitRelativePath;

            const fileData = await readFileAsync(file);
            zip.file(relativePath, fileData);
        }

        const zipBlob = await zip.generateAsync({ type: "blob" });

        archiveFormData.append("files", zipBlob);

        axios_tenant
        .post("/qc/archive", archiveFormData)
            .catch(err => {
                toast.error("Failed to create archive");
            });
    };

    const generateSummary = resultArray => {
        let errorResults = [];
        let correctValues = 0;

        resultArray.forEach(result => {
            if (result.isCorrect !== undefined) {
                if (result.isCorrect) correctValues++;
                else errorResults.push(result);
            } else {
                if (!!result.foundValue) correctValues++;
                else errorResults.push(result);
            }
        });

        setResults(errorResults);
        return errorResults;
    };

    const handleFileChange = files => {
        setUploadedFiles(files);
    };

    const generateExcel = async (resultArray = [], applicationNo) => {
        workbook = new ExcelJS.Workbook();

        const worksheet = workbook.addWorksheet("QC Results", { views: [{ showGridLines: false }] });

        const currentDate = new Date();

        const dataArray = [["Application No.:", applicationNo], ["QC Date:", `${currentDate.toLocaleDateString()} ${currentDate.toLocaleTimeString()}`], ["QC by:", localStorage.getItem("tenant_username")], [], [], ["Document Name", "Discrepancy", "Value Found", "Correct Value"]];

        dataArray.map((p, i) => worksheet.addRow(p));

        for (let i = 1; i <= 3; i++) {
            const row = worksheet.getRow(i);
            row.eachCell(cell => {
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" }
                };
            });
        }

        const row = worksheet.getRow(6);
        row.eachCell(cell => {
            cell.font = { bold: true };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" }
            };
        });

        for (const p of resultArray) {
            const row = worksheet.addRow([p.category, p.name, p.foundValue, p.trueValue]);

            for (let i = 1; i <= 4; i++) {
                const cell = row.getCell(i);

                if (p.isCorrect !== undefined) {
                    if (p.isCorrect) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "B7E1CD" }
                        };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    } else {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "E57373" }
                        };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    }
                } else {
                    if (!!p.foundValue) {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "B7E1CD" }
                        };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    } else {
                        cell.fill = {
                            type: "pattern",
                            pattern: "solid",
                            fgColor: { argb: "E57373" }
                        };
                        cell.border = {
                            top: { style: "thin" },
                            left: { style: "thin" },
                            bottom: { style: "thin" },
                            right: { style: "thin" }
                        };
                    }
                }
            }
        }

        worksheet.columns.forEach(column => {
            column.width = 30;
        });

        setNewWorkbook(workbook);
    };

    const handleExcelDownload = () => {
        newWorkbook &&
            newWorkbook.xlsx
                .writeBuffer()
                .then(buffer => {
                    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `QC_Results_${appNo}.xlsx`;

                    a.click();
                    toast.success("QC Results excel file downloaded");
                })
                .catch(err => {
                    toast.error("Failed to download excel file");
                });
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleFileChange,
        multiple: true
    });

    const dropzoneStyle = {
        border: "2px dashed #cccccc",
        borderRadius: "4px",
        padding: "5%",
        textAlign: "center",
        cursor: "pointer"
    };

    const handleClose = open => {
        setOpen(open);
    };

    const handleConfirmation = confirm => {

        if (confirm === true) {
            setLoading(1); //setting loading  value to true
            verifyRequest();
        } else {
            setLoading(3);
        }

        setOpen(false);
    };

    return (
        <>
            <Box display="flex" justifyContent="center">
                <FormControl sx={{ boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.2)", p: "1.5%", borderRadius: "8px", width: "50%" }}>
                    {/* <Typography  variant="h4" style={{ marginBottom: '1%', textAlign: 'center' }}>Add Data</Typography> */}

                    <TextField sx={{ mb: 1 }} id="file_name" label="Publication No./Application No." value={textData} onChange={handleTextChange} />
                    <div style={{ fontSize: "80%", color: "red" }}>*Publication No. format : WO2022000000 or WO/2022/000000</div>
                    <div style={{ fontSize: "80%", color: "red" }}>*Application No. format : PCTGB2021000000 or PCT/GB2021/000000 </div>
                    <br></br>

                    <Div sx={dropzoneStyle} {...getRootProps()}>
                        <input ref={inputRef} type="file" {...getInputProps()} webkitdirectory="true" multiple />
                        <p>Drag & drop QC package here or click to select a QC package</p>
                    </Div>
                    {uploadedFiles.length > 0 && <Typography variant="h6"> Selected {uploadedFiles.length} file(s)</Typography>}

                    <Button variant="contained" onClick={loading !== 1 || loading === 3 ? handleUpload : () => {}} sx={{ width: "15%", mt: "1%" }} disabled={!(textData && uploadedFiles.length > 0) || loading === 1}>
                        {loading === 1 ? <CircularProgress sx={{ color: "blue" }} /> : <span>Submit</span>}
                    </Button>

                    <Dialog
                        open={open}
                        // onClose={() => handleClose(false)}
                        aria-labelledby="confirmation-dialog-title"
                        aria-describedby="confirmation-dialog-description"
                    >
                        <DialogTitle id="confirmation-dialog-title">
                            <span style={{ color: "red", margin: "auto" }}>Alert!</span>
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="confirmation-dialog-description">
                                The following documents are missing :<br></br> <br></br>
                                {message}
                                <br></br> <br></br>
                                Do you still want to proceed?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleConfirmation(false)} color="primary" style={{ border: "2px solid #000", borderRadius: "4px" }} autoFocus>
                                No
                            </Button>
                            <Button onClick={() => handleConfirmation(true)} color="primary">
                                Yes
                            </Button>
                        </DialogActions>
                    </Dialog>
                </FormControl>
            </Box>

            <Box sx={{ marginTop: "3%" }}>
                {loading === 1 && (
                    <Typography variant="h1" style={{ marginBottom: "1%", textAlign: "center" }}>
                        Generating Results...
                    </Typography>
                )}

                {loading === 2 && (
                    <>
                        <Typography variant="h1" style={{ marginBottom: "1%", textAlign: "center" }}>
                            Summary Table
                        </Typography>

                        <Typography variant="h6" style={{ marginBottom: "1%", textAlign: "center", cursor: "pointer" }} onClick={handleExcelDownload}>
                            <Link>Click here to download the detailed report of QC Results</Link>
                        </Typography>

                        <Div sx={{ display: "flex", justifyContent: "center" }}>
                            <ResultsSummaryTable results={results} />
                        </Div>
                    </>
                )}

                {/* {loading===3 && (
       <Typography  variant="h1" style={{ marginBottom: '1%', textAlign: 'center' , color: "red"}}>Error while generating results</Typography>
       )} */}
            </Box>
        </>
    );
};

export default QC_FileUpload;